import { normalizeBrandColors } from './brandPalette.js'

export const LOGO_MAX_BYTES = 2 * 1024 * 1024
export const LOGO_MAX_WIDTH = 4096
export const LOGO_MAX_HEIGHT = 4096
export const LOGO_MAX_PIXELS = 4096 * 4096
export const LOGO_ACCEPTED_TYPES = Object.freeze(['image/png', 'image/jpeg', 'image/webp'])

function stringValue(...values) {
  const value = values.find((candidate) => typeof candidate === 'string' && candidate.trim())
  return value?.trim() || ''
}

export function normalizeSessionBranding(source = {}) {
  source = source && typeof source === 'object' ? source : {}
  const nested = source.branding && typeof source.branding === 'object' ? source.branding : {}
  const logo = nested.logo && typeof nested.logo === 'object'
    ? nested.logo
    : source.logo && typeof source.logo === 'object'
      ? source.logo
      : {}
  return {
    logoUrl: stringValue(logo.url, nested.logoUrl, nested.eventLogoUrl, source.logoUrl, source.eventLogoUrl),
    logoAltText: stringValue(nested.logoAltText, nested.logoAlt, source.logoAltText, source.logoAlt),
    clientName: stringValue(nested.clientName, nested.eventClientName, source.clientName, source.eventClientName),
    showOnStage: Boolean(nested.showOnStage ?? nested.showLogoOnStage ?? source.showOnStage ?? source.showLogoOnStage),
    version: stringValue(logo.version, nested.logoVersion, source.logoVersion),
    contentType: stringValue(logo.contentType, nested.logoContentType, source.logoContentType),
    bytes: Number(logo.bytes ?? nested.logoBytes ?? source.logoBytes ?? 0),
    width: Number(logo.width ?? nested.logoWidth ?? source.logoWidth ?? 0),
    height: Number(logo.height ?? nested.logoHeight ?? source.logoHeight ?? 0),
    fontFamily: stringValue(nested.fontFamily, source.fontFamily),
    stylesheetUrls: Array.isArray(nested.stylesheetUrls ?? source.stylesheetUrls) ? [...(nested.stylesheetUrls ?? source.stylesheetUrls)] : [],
    inlineCss: stringValue(nested.inlineCss, source.inlineCss),
    colors: normalizeBrandColors(nested.colors ?? source.colors),
  }
}

export function logoAltText(branding, fallback = 'Event client') {
  const normalized = normalizeSessionBranding(branding)
  return normalized.logoAltText || `${normalized.clientName || fallback} logo`
}

export function safeLogoUrl(value, { allowBlob = false, base = globalThis.location?.origin || 'https://localhost' } = {}) {
  if (typeof value !== 'string' || !value.trim() || value.length > 4096) return ''
  const candidate = value.trim()
  if (allowBlob && candidate.startsWith('blob:')) return candidate
  try {
    const parsed = new URL(resolveApiAssetUrl(candidate, base), base)
    if (parsed.username || parsed.password) return ''
    if (parsed.protocol === 'https:') return parsed.href
    if (parsed.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname)) return parsed.href
  } catch {
    return ''
  }
  return ''
}

function isPng(bytes) {
  const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
  return signature.every((value, index) => bytes[index] === value)
}

function isWebP(bytes) {
  return bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
}

function isJpeg(bytes) {
  return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
}

async function defaultDimensionReader(file) {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file)
    const dimensions = { width: bitmap.width, height: bitmap.height }
    bitmap.close?.()
    return dimensions
  }

  const objectUrl = URL.createObjectURL(file)
  try {
    return await new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
      image.onerror = () => reject(new Error('The logo image could not be decoded.'))
      image.src = objectUrl
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export async function validateLogoFile(file, readDimensions = defaultDimensionReader) {
  if (!file) throw new Error('Choose a PNG, JPEG, or WebP logo.')
  if (!LOGO_ACCEPTED_TYPES.includes(file.type)) throw new Error('Logo must be a PNG, JPEG, or WebP file.')
  if (!Number.isFinite(file.size) || file.size <= 0) throw new Error('The selected logo file is empty.')
  if (file.size > LOGO_MAX_BYTES) throw new Error('Logo must be 2 MB or smaller.')

  const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer())
  const signatureMatches = file.type === 'image/png' ? isPng(bytes) : file.type === 'image/jpeg' ? isJpeg(bytes) : isWebP(bytes)
  if (!signatureMatches) throw new Error('The file contents do not match the selected image type.')

  let dimensions
  try {
    dimensions = await readDimensions(file)
  } catch {
    throw new Error('The logo image could not be decoded.')
  }
  const width = Number(dimensions?.width)
  const height = Number(dimensions?.height)
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) throw new Error('The logo has invalid dimensions.')
  if (width > LOGO_MAX_WIDTH || height > LOGO_MAX_HEIGHT || width * height > LOGO_MAX_PIXELS) {
    throw new Error(`Logo dimensions must not exceed ${LOGO_MAX_WIDTH} × ${LOGO_MAX_HEIGHT} pixels.`)
  }
  return { width, height, bytes: file.size, type: file.type }
}

export function logoInitials(clientName) {
  const parts = String(clientName || '').trim().split(/\s+/).filter(Boolean)
  return (parts.map((part) => part[0]).slice(0, 2).join('') || 'EV').toUpperCase()
}
import { resolveApiAssetUrl } from '../api/urls.js'
